import { Formik } from "formik";
import { useRef, useState, useEffect } from "react";
import CMSFormTelephone from "../formFields/CMSFormTelephone";
import CMSFormInput from "../formFields/CMSFormInput";
import CMSFormSelect from "../formFields/CMSFormSelect";
import GeneralButton from "../common/GeneralButton";
import ContainerLoading from "../common/loading/ContainerLoading";
import { useToast } from "../contexts/ToastContext";
import { useNavigate } from "react-router-dom";
import { TiChevronLeft, TiChevronRight } from "react-icons/ti";
import { useCart } from "../contexts/CartContext";
import axios from "../../config/axios";
import { expressCheckoutSchema } from "../utils/validations";

const ExpressCheckoutAddressForm = ({ onCitySelect, onBack, cart }) => {
  const [status, setStatus] = useState("idle");
  const { updateDeliveryChargeByCity, setSelectedCity } = useCart();
  const [error, setError] = useState("");
  const [initialValues, setInitialValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    address: "",
    area: "",
    landmark: "",
    city: "",
    country: "UAE",
  });

  const { addToast } = useToast();
  const navigate = useNavigate();

  // Refs for scrolling to first error
  const refs = {
    firstName: useRef(null),
    lastName: useRef(null),
    email: useRef(null),
    mobile: useRef(null),
    address: useRef(null),
    area: useRef(null),
    landmark: useRef(null),
    city: useRef(null),
    country: useRef(null),
  };

  // ✅ Load saved form data on mount
  useEffect(() => {
    try {
      const guestUserDetails = localStorage.getItem("guestUserDetails");
      const savedAddress = localStorage.getItem("expressCheckoutAddress");

      if (guestUserDetails || savedAddress) {
        const parsedGuest = JSON.parse(guestUserDetails);
        const parsedAddress = JSON.parse(savedAddress);

        // console.log("Guest user details:", guestUserDetails,savedAddress);
        if (parsedAddress?.city) {
          setSelectedCity(parsedAddress?.city);
          updateDeliveryChargeByCity(parsedAddress?.city);
          setTimeout(() => {
            if (onCitySelect) onCitySelect();
          }, 500);
        }

        setInitialValues((prev) => ({
          ...prev,
          firstName: parsedGuest?.firstName || parsedAddress?.firstName || "",
          lastName: parsedGuest?.lastName || parsedAddress?.lastName || "",
          email: parsedGuest?.email || parsedAddress?.email || "",
          mobile: parsedGuest?.mobile || parsedAddress?.mobile || "",
          ...parsedAddress,
          cartId: cart?.orderCart?._id || "",
        }));
      }
    } catch (err) {
      console.warn("⚠️ Failed to load saved checkout details:", err);
    }
  }, [cart?.orderCart?._id]);

  // Scroll helper
  const scrollToError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField && refs[firstErrorField]?.current) {
      refs[firstErrorField].current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      const el = refs[firstErrorField].current.querySelector(
        "input,select,textarea",
      );
      if (el) el.focus();
    }
  };

  // ✅ API submission
  const submitAllDetails = async (values) => {
    setStatus("loading");
    try {
      // Save locally for persistence
      localStorage.setItem("expressCheckoutAddress", JSON.stringify(values));

      await axios.post("/cart/add-guest-shipping-address-to-product-order", {
        cartId: cart?.orderCart?._id || "",
        ...values,
      });

      setStatus("success");
      addToast("Receiver details submitted successfully", "success");
      navigate("/payment/order");
    } catch (err) {
      console.error(err);
      setStatus("idle");
      setError("Submission failed, please try again");
      setTimeout(() => {
        setError("");
      }, 5000);
    }
  };

  return (
    <>
      <div className="rounded-[15px] relative min-h-[300px]">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={expressCheckoutSchema}
          onSubmit={submitAllDetails}
        >
          {(form) => (
            <div>
              <div className="mt-6 p-10 2.5xl:p-20 bg-[#DEE2DD]/50 rounded-[15px] relative min-h-[300px]">
                <p className="text-21pt mb-4 md:mb-6 lg:mb-8 text-green">
                  Add Delivery Details
                </p>
                {status === "loading" && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10">
                    <ContainerLoading />
                  </div>
                )}

                <div className="w-full grid grid-cols-1 gap-8">
                  {/* Personal Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div ref={refs.firstName}>
                      <CMSFormInput
                        name="firstName"
                        label="Receiver's First Name"
                        form={form}
                        required
                      />
                    </div>
                    <div ref={refs.lastName}>
                      <CMSFormInput
                        name="lastName"
                        label="Receiver's Last Name"
                        form={form}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div ref={refs.email}>
                      <CMSFormInput
                        name="email"
                        label="Receiver's Email Address"
                        form={form}
                        required
                      />
                    </div>
                    <div ref={refs.mobile}>
                      <CMSFormTelephone
                        name="mobile"
                        label="Mobile"
                        smallDescription="(with WhatsApp enabled)"
                        form={form}
                        required
                      />
                    </div>
                  </div>

                  {/* Address Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div ref={refs.address}>
                      <CMSFormInput
                        name="address"
                        label="Receiver's Full Address"
                        form={form}
                        required
                      />
                    </div>
                    <div ref={refs.area}>
                      <CMSFormInput
                        name="area"
                        label="Receiver's Area"
                        form={form}
                        required
                      />
                    </div>
                    <div ref={refs.landmark}>
                      <CMSFormInput
                        name="landmark"
                        label="Receiver's Landmark"
                        form={form}
                      />
                    </div>
                    <div ref={refs.city}>
                      <CMSFormSelect
                        name="city"
                        label="Receiver's City"
                        form={form}
                        selected={form.values.city}
                        onChange={(e) => {
                          form.handleChange(e);
                          updateDeliveryChargeByCity(e.target.value);
                          setSelectedCity(e.target.value);
                          if (onCitySelect) onCitySelect();
                          // Save updated city to localStorage
                          const updated = {
                            ...form.values,
                            city: e.target.value,
                          };
                          localStorage.setItem(
                            "expressCheckoutAddress",
                            JSON.stringify(updated),
                          );
                        }}
                        placeholder={{ label: "Select City", value: "" }}
                        options={[
                          { label: "Abu Dhabi", value: "Abu Dhabi" },
                          { label: "Ajman", value: "Ajman" },
                          { label: "Dubai", value: "Dubai" },
                          { label: "Fujairah", value: "Fujairah" },
                          {
                            label: "Ras Al Khaimah",
                            value: "Ras Al Khaimah",
                          },
                          { label: "Sharjah", value: "Sharjah" },
                          { label: "Umm Al Quwain", value: "Umm Al Quwain" },
                        ]}
                        required
                      />
                    </div>
                    <div ref={refs.country}>
                      <CMSFormInput
                        name="country"
                        label="Receiver's Country"
                        form={form}
                        disabled
                        required
                      />
                    </div>
                  </div>
                  {error && (
                    <div
                      className={`relative  transition-all duration-300 ease-in-out my-3 ${
                        error
                          ? "opacity-100 -translate-y-3"
                          : "opacity-0 -translate-y-2"
                      }`}
                    >
                      {error && <span className="errorMessage">{error}</span>}
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex items-end justify-between mt-9">
                <GeneralButton
                  onClick={() => {
                    onBack();
                    updateDeliveryChargeByCity("");
                    setSelectedCity("");
                  }}
                  text="Prev step"
                  title=""
                  iconPosition="Left"
                  bgColor="#ccc"
                  textColor="#000"
                  icon={<TiChevronLeft size={24} className="me-4" />}
                  className="text-24pt"
                />
                <GeneralButton
                  onClick={() => {
                    form.setTouched(
                      Object.keys(form.initialValues).reduce((acc, key) => {
                        acc[key] = true;
                        return acc;
                      }, {}),
                    );

                    form.validateForm().then((errors) => {
                      if (Object.keys(errors).length > 0) {
                        scrollToError(errors);
                      } else {
                        form.handleSubmit();
                      }
                    });
                  }}
                  text="Next step"
                  type="button"
                  title=""
                  bgColor="#30160E"
                  textColor="#ccc"
                  icon={<TiChevronRight size={24} className="ms-4" />}
                  className="text-24pt"
                />
              </div>
            </div>
          )}
        </Formik>
      </div>
    </>
  );
};

export default ExpressCheckoutAddressForm;
